import { addSpacesToTitleCase, enumKeyOf, enumKeys } from "common/helpers/utils";
import Guid from "common/values/guid/guid";
import { PermissionsByGranteeId } from "permissions/entities/permission/api/permissions-api-service";
import Permission from "permissions/entities/permission/permission";
import { AccessLevel } from "permissions/entities/permission/resources/general";
import { WorkAgreementAccess, WorkAgreementField } from "permissions/entities/permission/resources/work-resources";
import { CardType, GroupedCellConfig, MetaControlType } from "permissions/entities/permission/view/components/permissions-table/types";
import { getWorkAgreementName, isPermissionsRow, isTemplateRow } from "permissions/entities/permission/view/components/permissions-table/utils";
import { JSX } from "react";
import BarebonesWorkAgreementInfoAPIResponse from "work/entities/work-agreement/api/request-contracts/barebones-work-agreement-api-response";
import { WorkAgreementError } from "work/entities/work-agreement/store/work-agreement-redux-slice";

export default function WorkAgreementGroupCell({
  row,
  loadingWorkAgreementIds,
  allWorkAgreementErrors,
  barebonesWorkAgreements,
  renderGroupedPermission,
}: {
  row: PermissionsByGranteeId | null;
  loadingWorkAgreementIds: Guid[];
  allWorkAgreementErrors: WorkAgreementError[];
  barebonesWorkAgreements: BarebonesWorkAgreementInfoAPIResponse[];
  renderGroupedPermission: (row: PermissionsByGranteeId, config: GroupedCellConfig) => JSX.Element;
}): JSX.Element | null {
  if (!row?.recentPermissionsPreview) return null;
  // Determine dynamic title (single work agreement -> show its name)
  const workAgreementResources = row.recentPermissionsPreview
    .filter((p): p is Permission & { resource: WorkAgreementAccess } => p.resource instanceof WorkAgreementAccess)
    .map(p => p.resource);

  const uniqueWorkIds = Array.from(new Set(workAgreementResources.map(r => r.workId?.value)));
  const title =
    uniqueWorkIds.length === 1 && workAgreementResources[0]
      ? getWorkAgreementName(workAgreementResources[0], loadingWorkAgreementIds, allWorkAgreementErrors, barebonesWorkAgreements)
      : undefined;

  return renderGroupedPermission(row, {
    cardType: CardType.Grouped,
    keys: enumKeys(WorkAgreementField).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    candidatesForKey: (row, waKey) => {
      if (isPermissionsRow(row)) {
        return row.recentPermissionsPreview.filter((permission) => {
          if (!(permission.resource instanceof WorkAgreementAccess)) return false;
          const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
          const fields = permission.resource.fields ?? {};
          return Object.hasOwn(fields, fieldKey);
        });
      }
      if (isTemplateRow(row)) {
        return row.resources.filter((resource) => {
          if (!(resource instanceof WorkAgreementAccess)) return false;
          const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
          const fields = resource.fields ?? {};
          return Object.hasOwn(fields, fieldKey);
        });
      }
      return [];
    },
    labelForKey: (waKey) => addSpacesToTitleCase(waKey),
    controlMetaForResource: (resource, waKey) => {
      const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
      const fields = (resource as WorkAgreementAccess).fields as Partial<Record<WorkAgreementField, AccessLevel>> ?? {};
      const current = fields[fieldKey];
      const opts = enumKeys(AccessLevel);
      const value = current ? enumKeyOf(AccessLevel, current) : opts[0];
      return { type: MetaControlType.AccessLevel, label: "Access Level", options: opts, value };
    },
    title,
    colSpan: 2,
    rowSpan: 2,
    canEdit: true
  });
}
